import { defineStore } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { biddingSource } from '../sdk/biddingSource.js';
import { sessionStore } from '@hds/identity-client-sdk';
import { assetCardForLot, detailDocForLot } from '../sdk/lotProjection.js';
import { requestTimestamp } from '../sdk/requestLogging.js';
import { useLogger, useNotifications } from '../adapters/ports.js';
import { likedStore } from './liked.store.js';
import vehicleCacheStore from './vehicle-cache.store.js';

// Event-level runtime states, as written by the engine's event orchestrators
// (all three share this enum). Note RESUME sets 'RESUMED', NOT back to
// 'STARTED' — so "is it live" is `!== PAUSED`, never `=== STARTED`.
const EVENT_PAUSED = 'PAUSED';
const EVENT_FINISHED = 'FINISHED';

/**
 * Live Bidding store — the lot-state model for the Live Bidding surface
 * (home list → sales → lanes → lot cards → bid tray → live lane video).
 *
 * Data source: the client SDK's `biddingSource` — vibe-server's sales and their
 * assets for what is sold, and the REAL event runtime for the rest: bidding-server
 * reads, a delta-server change feed for live updates, and bids written to
 * bidding-server. `loadLots()` pulls each sale and its run-list lots; live bid changes arrive as lot frames
 * that drive the reactive `liveBids` map; bids / proxies are written back through
 * the source. There is no mock floor timer — every change comes from the engine.
 *
 * The component layer never talks to the runtime directly — it only reacts to
 * `lots` + `liveBids`, so the surface is unchanged from the previous mock.
 */

// The "bid higher" chips offered in the confirm tray are these multiples of the
// lot's own increment (increment 250 → +250, +500, +1k, +2k). The smallest chip
// is one increment — the minimum legal raise.
export const BID_INCREMENT_MULTIPLIERS = [1, 2, 4, 8];

// Engine bidding states that mean the lot is finished and should leave the
// live lanes.
const FINALIZED = new Set(['SOLD', 'SOLD-BUYNOW', 'NOT-SOLD', 'OFFER', 'ABORTED']);

// Engine bidding states that mean a lot is actively on the block right now — the
// clerk has it up for bidding. Distinct from NOT-STARTED (upcoming) and
// FINALIZED (done). This is the runtime's authoritative "current lot" signal;
// reading it lets the on-block marker follow the clerk through the run order,
// forward when a lot hammers AND backward when they step back to an earlier
// vehicle, instead of assuming lots always sell strictly front-to-back.
const LIVE_ON_BLOCK = new Set(['ASKING', 'STARTED', 'BIDDING', 'ANTI-SNIPE']);

// Map a server lot row (GET /bidding/live-events) to the lot shape the Live
// Bidding components expect.
function mapServerLot(row) {
  const estMs = row.estimatedTime?.utcTime ? Date.parse(row.estimatedTime.utcTime) : null;
  const secs = estMs ? Math.max(0, Math.round((estMs - Date.now()) / 1000)) : 0;
  return {
    id: row.id,
    itemId: row.itemId ?? row.id,
    eventId: row.eventId ?? null,
    sale: row.sale,
    type: row.type,
    lane: row.lane,
    lot: row.lot,
    year: row.year,
    make: row.make,
    model: row.model,
    trim: row.trim,
    mileage: row.mileage ?? null,
    vin: row.vin ?? null,
    currentBid: row.current ?? row.ask ?? 0,
    askingPrice: row.ask ?? 0,
    // `null` rather than an invented $100 — see the note at the source's own
    // `increment`. An asset with no increment has not started, so it has no
    // price either, and the dash covers both.
    bidIncrement: row.increment ?? row.inc ?? null,
    mmr: row.mmr ?? 0,
    secondsRemaining: secs,
    endsAt: estMs,
    status: null,
    lights: row.lights ?? [],
    imageUrl: row.img,
    fallback: row.fallback,
    assetId: row.assetId,
    target: Math.round((row.current ?? row.ask ?? 0) + (row.mmr ? Math.max(0, row.mmr - (row.current ?? row.ask ?? 0)) : 5000)),
    startAt: null,
    engineStatus: row.engineStatus ?? 'NOT-STARTED',
    badge: row.badge ?? null,   // winning bidder (kept for the Bid Stack's Won/Lost)
    scheduled: false
  };
}

export const biddingStore = defineStore('biddingStore', () => {
  const saved = likedStore();
  const vehicleCache = vehicleCacheStore();

  // The lot list comes from the event runtime (see loadLots). Live state
  // overlays the seed once the dealer/floor acts.
  const lots = ref([]);
  const loaded = ref(false);
  const loading = ref(false);
  // Whether the LAST loadLots() ended in the catch below.
  //
  // Without it a failed load and an empty sale are the same thing to a caller:
  // `lots` is empty and `loading` is false in both cases, so a surface asking
  // "is there anything live?" answers "no" to a network error and tells the
  // dealer nothing is running when the truth is that nobody asked the runtime
  // successfully. It is the load's own outcome rather than a general error
  // channel — every start clears it, so it never describes a stale attempt.
  const loadFailed = ref(false);
  // Bumped after a server-side sale reset (`lots.reset`) reloads a fresh block —
  // a signal consumers (e.g. the Bid Stack showcase) watch to re-run themselves.
  const resetSeq = ref(0);
  // ALL lots from the feed (finalized included), never pruned — the Bid Stack
  // derives its closed/on-block/upcoming + Won/Lost straight from here. Live
  // Bidding keeps using the filtered `lots` (finalized lots are spliced out of
  // it). `engineById` indexes the same objects for O(1) frame updates.
  const engineLots = ref([]);
  const engineById = new Map();
  // The live subset (`lots`) indexed by id. Frames arrive for every lot in the
  // block, so resolving the lot a frame names must not scan the array — see
  // findLot(). Kept in step with `lots` in loadLots() and at the finalize splice.
  const liveById = new Map();

  // ── Per-lot live state (overrides the seed once the dealer/floor acts) ──
  // Event-level runtime status, keyed by EVENT id (a few dozen keys, not the
  // ~1300 of a lot map) — so a pause frame invalidates one key and every card in
  // that sale re-reads it, rather than being fanned out onto every lot.
  const eventStatuses = reactive({});  // eventId -> 'STARTED' | 'PAUSED' | 'RESUMED' | 'FINISHED'
  const eventHaltReasons = reactive({});  // eventId -> the clerk's reason for the CURRENT halt
  const liveBids = reactive({});     // id -> { current, status: 'winning' | 'outbid' | null }
  // Armed proxy maxes, keyed per lot AND per badge. A dealer holds several
  // badges and the runtime keeps one proxy row per (item, badge), so one slot
  // per lot made badge A's proxy overwrite badge B's on the same lot: arming A
  // took over B's amount, and cancelling A deleted the only entry the app knew
  // about. Every read and write goes through the per-badge helpers below
  // `badgeFor`, which address the badge the dealer bids THAT lot as and leave
  // the sibling badges' entries alone.
  const maxBids = reactive({});      // id -> { [badge]: number } (armed proxy maxes)
  // The clerk can raise or lower an asset's increment while it runs, and the
  // frame that carries it used to be applied in silence — the chips, the next
  // bid and the slide amount all moved under the dealer's thumb with no word
  // said. This is the record a surface says it with: the last change per asset,
  // with when it landed, so each surface decides how long to show it and none
  // of them keeps a timer in the store.
  const incrementChanges = reactive({});   // id -> { previous, next, at }
  const identities = reactive({});   // id -> identity object (bidding-as)
  // The dealerships this dealer may bid as, and the badge each bids under.
  //
  // Read off the session (the Human's `bidderIdentities`), never a constant here.
  // The runtime takes the badge from each request and accepts any badge from an
  // authenticated bidder until CGAC/FGAC, so this list is what keeps the picker
  // honest. Empty when signed out: there is nobody to bid as.
  const bidIdentities = ref([]);

  // Membership of the winning / outbid views, mirrored off `liveBids` as bids
  // land rather than re-derived by scanning it.
  //
  // Why not just call effStatus(lot) from matchesStat / statCards: doing so reads
  // `liveBids[id]` for every lot in the block, which subscribes those computeds
  // to ~1300 individual reactive keys — so EVERY inbound frame, including frames
  // for lots nobody is filtering on, invalidates them and rebuilds the whole
  // sale → lane → lots tree (re-rendering every mounted pager). A reactive Set
  // notifies only when a key is genuinely added or removed (Vue's Set add/delete
  // no-op when the key is already present/absent), so an ordinary bid frame that
  // doesn't change the dealer's standing costs nothing here.
  //
  // These stay equivalent to effStatus() because `lot.status` is seeded null by
  // mapServerLot and never assigned — `liveBids` is the only source of status.
  const winningIds = reactive(new Set());
  const outbidIds = reactive(new Set());

  // The single write path for `liveBids`, so the sets above can never drift from
  // it. Every assignment to liveBids goes through here.
  //
  // Membership tracks every lot in `lots` (finalized included, since finalized
  // lots now stay in their lanes and count — a won lot the dealer took stays
  // under Winning, a lost one under Outbid). liveById is the authority on
  // membership; the `isLive` guard just rejects a stray frame for a lot not in
  // this block. Cleared and rebuilt wholesale by loadLots on a fresh block.
  function setLiveBid(id, current, status) {
    liveBids[id] = { current, status };
    const isLive = liveById.has(id);
    if (isLive && status === 'winning') { winningIds.add(id); outbidIds.delete(id); }
    else if (isLive && status === 'outbid') { outbidIds.add(id); winningIds.delete(id); }
    else { winningIds.delete(id); outbidIds.delete(id); }
  }

  // The one eraser, for the same reason setLiveBid is the one writer: an asset
  // nobody has bid on has NO entry, and a bid the engine refuses has to leave
  // it that way rather than leaving a `{ current: null }` behind that reads as
  // a price of nothing. Used only by placeBid's undo.
  function clearLiveBid(id) {
    delete liveBids[id];
    winningIds.delete(id);
    outbidIds.delete(id);
  }

  // A 1-second clock that drives live lot countdowns between server frames.
  // Started with the realtime subscription and stopped in teardown.
  const nowTick = ref(Date.now());
  let tickHandle = null;

  // Bumped on every placed bid so an open tray/dock can fire a fresh confetti.
  const confettiBump = ref(0);

  // The dealer's last-chosen "bid higher" multiple (an index into
  // BID_INCREMENT_MULTIPLIERS), kept here so it persists when the bid tray is
  // torn down and reopened between bids. Stored as the MULTIPLIER rather than an
  // absolute amount, so it stays valid across lots with different increments.
  //
  // It reads like presentation and is not: `raiseTo` is built from it, and
  // raiseTo is the amount the runtime would receive. What the dealer intends to
  // bid is a bidding fact.
  const bidIncrementMultiplier = ref(1);

  // Lots the dealer has bid on — drives winning/outbid resolution against the
  // badge reported by the runtime.
  const myBids = reactive(new Set());

  // Fire-once guards so repeated frames for the same lot don't re-notify for a
  // win or an ending countdown. (Outbid self-dedupes: it only fires on the
  // winning→outbid transition, so a run of `outbid` frames won't repeat.)
  const wonNotified = new Set();
  // Assets whose final outcome has been logged. Separate from `wonNotified`
  // because that one only tracks wins, and a loss is the more interesting event.
  const resultLogged = new Set();
  const endingNotified = new Set();
  // Favorited lots already alerted as "on the block" — fire-once, mirrors the
  // two guards above. Cleared on a demo reset (reloadBlock) and re-seeded by
  // loadLots for whatever is already on the block, so a fresh block never
  // barrages the dealer.
  const liveNotified = new Set();
  // Seconds-to-hammer under which a lot the dealer cares about is "ending".
  const ENDING_SOON_SECS = 10;

  // The most recent alert relevant across sales (outbid / won / ending / a
  // liked lot going live), published for the Live page's "jump to that
  // sale" banner. Plain data with no knowledge of the selection layer, so the
  // notification path stays fully global and decoupled.
  const lastAlert = ref(null);
  function setLastAlert(id, kind) {
    const sale = engineById.get(id)?.sale;
    if (sale) lastAlert.value = { sale, kind, lotId: id, at: Date.now() };
  }

  // ── Lookups & derivations ──────────────────────────────────────────────
  const laneKey = (lot) => `${lot.sale}::${lot.lane}`;
  const findLot = (id) => liveById.get(id) ?? null;

  // Lane membership, indexed once per `lots` mutation rather than re-derived per
  // lot. Every lot card asks "am I on the block?" and "how many vehicles away am
  // I?" as it renders, and answering those by filtering the whole lot array made
  // the page quadratic in block size — ~3.1M element visits per second at 1300
  // lots, at idle.
  //
  // These read only `sale` / `lane` / `scheduled` / `id`. Vue tracks reactive
  // objects per-property, so the in-place `bid` / `status` / `engineStatus`
  // writes an inbound frame makes do NOT invalidate them. Keep it that way:
  // reading a frame-mutated field here would rebuild all three maps on every bid.
  const laneIndex = computed(() => {
    const lanes = new Map();
    for (const lot of lots.value) {
      const key = laneKey(lot);
      const lane = lanes.get(key);
      if (lane) lane.push(lot);
      else lanes.set(key, [lot]);
    }
    return lanes;
  });

  // laneKey -> id of the lot the runtime currently has on the block there. We
  // pick the lane's lot whose live engineStatus (ASKING/STARTED/BIDDING/
  // ANTI-SNIPE) says the clerk has it up — NOT the front lot in run order — so
  // the on-block marker FOLLOWS THE CLERK: forward when a lot hammers, backward
  // when they step back to re-run an earlier vehicle, and off a sold lot the
  // instant it leaves a live status. Falls back to the first lot in run order
  // when nothing is live yet (before the lane opens, or the gap between lots).
  //
  // Unlike laneIndex/awayById (kept on stable fields so a bid frame never
  // rebuilds them), this DOES read engineStatus — but that flips only on a
  // lot's lifecycle transitions (a handful per lot across the whole sale), not
  // per bid, and Vue skips the retrigger when a frame rewrites the same status,
  // so the rebuild stays rare.
  const onBlockByLane = computed(() => {
    const byLane = new Map();
    for (const [key, laneLots] of laneIndex.value) {
      // The lot the clerk has up, else the next lot still to run. Finalized lots
      // stay in the lane now, so the fallback must skip them — the marker never
      // lands on a sold/passed lot. When every lot is done, no lot is on block.
      const live =
        laneLots.find((l) => LIVE_ON_BLOCK.has(l.engineStatus)) ??
        laneLots.find((l) => !FINALIZED.has(l.engineStatus));
      if (live) byLane.set(key, live.id);
    }
    return byLane;
  });

  // lotId -> count of lots still to run ahead of it in its lane. Finalized lots
  // stay in the lane but have already run, so they don't count toward "N away"
  // (reading engineStatus here means this rebuilds on a lot's lifecycle
  // transitions, like onBlockByLane — a handful per lot, not per bid). A lot's
  // own `scheduled` flag deliberately does not gate its own count — a scheduled
  // lot still reports how many vehicles run before it.
  const awayById = computed(() => {
    const away = new Map();
    for (const laneLots of laneIndex.value.values()) {
      let ahead = 0;
      for (const lot of laneLots) {
        away.set(lot.id, ahead);
        if (!lot.scheduled && !FINALIZED.has(lot.engineStatus)) ahead += 1;
      }
    }
    return away;
  });

  // ── Event-level sale status ────────────────────────────────────────────
  // A clerk pausing a sale is an EVENT fact: no lot changes, no item-bidding
  // frame. Everything below reads the one status map so a card, a lane banner
  // and the bid tray can never disagree about whether a sale is running.
  const eventStatusFor = (lot) => (lot.eventId ? eventStatuses[lot.eventId] ?? null : null);
  // Paused is recoverable (the clerk can resume); finished is terminal. Both stop
  // the engine processing bids, so both block bidding — they differ only in how
  // they're presented.
  const isPaused = (lot) => eventStatusFor(lot) === EVENT_PAUSED;
  const isEventFinished = (lot) => eventStatusFor(lot) === EVENT_FINISHED;
  const isBiddingUnavailable = (lot) => isPaused(lot) || isEventFinished(lot);
  // Presentation for a paused or finished sale, or null while it's running.
  // Cards and banners both render off this so the wording and tone stay in one
  // place — it is exactly the shape SaleStatusBanner's `saleStatus` prop takes.
  // `line` stays the generic "what this means for you"; `reason` is the clerk's
  // own words and may be absent (an older pause, or the reason read hasn't
  // landed) — surfaces render it as an extra, never as the only explanation.
  const saleStatusReasonFor = (lot) => (lot.eventId ? eventHaltReasons[lot.eventId] ?? null : null);
  const saleStatusFor = (lot) =>
    isPaused(lot)
      ? { state: 'paused', label: 'PAUSED', line: 'Bidding is on hold', reason: saleStatusReasonFor(lot) }
      : isEventFinished(lot)
        ? { state: 'finished', label: 'ENDED', line: 'This sale has ended', reason: saleStatusReasonFor(lot) }
        : null;
  // Fork / mobile portal names for the same helpers. Cards, trays and banners
  // still call `haltMeta` / `isHalted` / `haltReasonFor`; keep those as aliases
  // so the org rename does not break existing callers or spies.
  const isHalted = isBiddingUnavailable;
  const haltReasonFor = saleStatusReasonFor;
  const haltMeta = saleStatusFor;

  // Effective current bid + status (live override wins over the seed).
  const liveCurrent = (lot) => liveBids[lot.id]?.current ?? lot.currentBid;
  const effStatus = (lot) => liveBids[lot.id]?.status ?? lot.status ?? null;

  // The badge the dealer is bidding as on a given lot.
  // Who the dealer bids as when a lot has no pick of its own. The Dashboard's
  // "bidding as" chrome sets it, the live surfaces' per-lot pickers still win
  // over it, and it is one of `bidIdentities` — never a name this store made up.
  const defaultIdentity = ref(null);
  function setDefaultIdentity(identity) {
    defaultIdentity.value = identity ?? null;
  }
  const badgeFor = (id) =>
    identities[id]?.badge ?? defaultIdentity.value?.badge ?? bidIdentities.value[0]?.badge ?? null;

  // ── The per-badge proxy slots (see `maxBids`) ───────────────────────────
  // The max armed under one named badge, and the max armed under the badge the
  // dealer bids this lot as — which is what every proxy read below answers
  // with, because that is the identity the next arm or cancel will address.
  const proxyMaxForBadge = (id, badge) => (badge == null ? undefined : maxBids[id]?.[badge]);
  const proxyMaxFor = (id) => proxyMaxForBadge(id, badgeFor(id));
  // Every badge's max on one lot, for a surface that reports the dealer's whole
  // cover rather than the active identity's.
  const proxyMaxesFor = (id) => maxBids[id] ?? null;
  // Writes that touch ONE badge's slot. A lot with no slots left loses its
  // entry outright, so `maxBids[id] != null` keeps meaning "this lot has a
  // proxy on it" for the surfaces that count lots.
  function writeProxyMax(id, badge, amount) {
    if (badge == null) return;
    const forLot = maxBids[id] ?? (maxBids[id] = {});
    forLot[badge] = amount;
  }
  function dropProxyMax(id, badge) {
    if (badge == null) return;
    const forLot = maxBids[id];
    if (!forLot) return;
    delete forLot[badge];
    if (!Object.keys(forLot).length) delete maxBids[id];
  }
  // True when a winning badge belongs to the dealer (any of their dealerships) —
  // i.e. the dealer took the lot. Simulated bidders bid as `SIM-*`, so this
  // cleanly separates a real win from a loss, with no reliance on local bid
  // history.
  const isDealerBadge = (badge) =>
    badge != null && bidIdentities.value.some((identity) => identity.badge === badge);
  // The runtime's high-bid badge is one of the dealer's badges (any of their
  // identities, won by a live bid or by a proxy) — read off the reactive engine
  // lot, so it survives a reload rather than relying on local bid history.
  const dealerLeads = (lot) => lot?.badge != null && isDealerBadge(lot.badge);
  const lotResult = (lot) => {
    if (!FINALIZED.has(lot.engineStatus)) return null;
    if (lot.engineStatus === 'ABORTED') return 'skipped';
    if (lot.engineStatus === 'NOT-SOLD') return 'noSale';
    if (lot.badge && isDealerBadge(lot.badge)) return 'won';
    if (myBids.has(lot.id)) return 'lost';
    return 'sold';
  };

  // On-block = the lot(s) currently up for bidding. Simulcast/automated auctions
  // run one lot at a time (the first non-scheduled lot in the (sale, lane)). A
  // timed auction runs every lot concurrently, so all non-finalized lots are
  // simultaneously on the block.
  const onBlock = (lot) =>
    lot.type === 'Timed'
      ? !FINALIZED.has(lot.engineStatus)
      : onBlockByLane.value.get(laneKey(lot)) === lot.id;

  // Whether the runtime will take a proxy on this asset yet.
  //
  // It will, from the moment the EVENT is running — which is the whole of what
  // the event orchestrators changed (#266 / #270). A proxy is written against
  // the event, not against the asset's own bidding record, so an asset still
  // waiting its turn takes one as a PREBID and the orchestrator carries it into
  // the asset's run. That is why NOT-STARTED belongs here rather than being
  // refused: the old refusal described a 404 the item-scoped write used to
  // answer with, and nothing writes that way any more.
  //
  // Two cases still take none. A settled asset, because its run is over. And an
  // asset the store does not hold, or holds with no event on it — the Bid Stack's
  // stand-in for a saved asset with no runtime lot — because an event-scoped
  // write has no event to address.
  const acceptsProxy = (lot) => {
    if (!lot) return false;
    const held = findLot(lot.id);
    return held !== null
      && held.eventId != null
      && !FINALIZED.has(lot.engineStatus);
  };

  // Running RIGHT NOW, which is narrower than `onBlock`: the marker also sits
  // on the next lot still to run, so a lane whose clerk has nothing up still
  // has an on-block lot. A surface that offers a live bid, rather than a place
  // in the queue, asks this. A timed lot takes bids for as long as it is open,
  // so for it the two are the same question.
  const isRunning = (lot) =>
    lot.type === 'Timed'
      ? onBlock(lot)
      : LIVE_ON_BLOCK.has(lot.engineStatus);

  // Remaining seconds until a lot closes, recomputed against the 1-second
  // `nowTick` clock so the countdown ticks live between server frames. Falls
  // back to the last server-provided `secs` when no close time is known.
  const secsLeft = (lot) =>
    lot.endsAt != null ? Math.max(0, Math.round((lot.endsAt - nowTick.value) / 1000)) : lot.secondsRemaining;

  // "Vehicles away" = count of non-scheduled lots ahead of it in the lane.
  const vehiclesAway = (lot) => awayById.value.get(lot.id) ?? 0;

  // Whether the dealer likes this lot's asset, live. The heart's own answer.
  const isLiked = (lot) =>
    lot.assetId != null && saved.likedAssetIds.has(String(lot.assetId));

  // Whether a ROW for this lot belongs on a list the dealer is looking at.
  //
  // Not the heart's answer. The heart is `isLiked`, the live truth, and it
  // empties the moment the asset is unliked. This one stays true through a
  // pending unlike, so a list derived from the liked set keeps the row where
  // the thumb left it until the dealer changes route (R2/R4).
  const isLikedForDisplay = (lot) =>
    lot.assetId != null && saved.isLikedForDisplay(String(lot.assetId));

  const toggleLike = (lot) =>
    isLiked(lot)
      ? saved.unlike(String(lot.assetId))
      : saved.like(assetCardForLot(lot));

  const seedVehicleDetail = (lot) => {
    vehicleCache.set(String(lot.assetId), detailDocForLot(lot));
    return lot.assetId;
  };

  // The last increment change on an asset, or null. A surface reads `at`
  // against `nowTick` to decide whether its notice is still current — the
  // store states the fact and times nothing.
  const incrementChangeFor = (lot) => incrementChanges[lot.id] ?? null;

  // `null` for an asset the engine has not given an increment to. Every figure
  // built on it answers `null` in turn rather than inventing a ladder: the one
  // proposal the engine takes with no increment is an opening bid AT THE ASK,
  // and any raise off a made-up increment is refused.
  const incFor = (lot) => lot.bidIncrement || null;
  // The amount a Bid tap commits — ALWAYS strictly above the live current bid.
  //
  // `ask` counts only while it still leads the current bid (an opening ask on a
  // lot nobody has bid yet). It cannot be trusted otherwise: the runtime feed has
  // no separate ask price, so buildRow() seeds `ask` from the same `bid` field as
  // `current`, and no lot.updated frame ever refreshes it. The old sentinel here
  // ("live value still equals the seed → use the ask") was permanently true for
  // the same reason — applyLotFrame writes the frame's bid to BOTH lot.currentBid
  // and liveBids[id].current — so a lot whose price climbed after page load kept
  // offering the frozen load-time ask, i.e. a bid UNDER the current bid.
  const bidAmount = (lot) => {
    const cur = liveCurrent(lot);
    if (lot.askingPrice > cur) return lot.askingPrice;
    const increment = incFor(lot);
    return increment === null ? null : cur + increment;
  };

  // The smallest proxy max that can actually fire. The engine ignores any proxy
  // at or under the live price (it filters `amount >= current + increment`), so a
  // max below this silently never auto-bids.
  //
  // `null` with no increment: there is no next valid bid to clear, so there is
  // no floor either — and a floor invented off $100 would disable the `−` at a
  // number that means nothing.
  const minProxy = (lot) => {
    const increment = incFor(lot);
    return increment === null ? null : liveCurrent(lot) + increment;
  };

  // The amount a proxy drawer opens at.
  //
  // An armed max the live price has already passed is DEAD — that is exactly what
  // being outbid through a proxy means — so re-seed it at the minimum that puts
  // the dealer back in the running rather than re-showing the max that just lost.
  // `fallback` is the drawer's own suggestion for a lot with no proxy armed yet
  // (mmr, so the default clears the autobidder ceilings); it is floored the same
  // way for lots already bid past it.
  const proxySeed = (lot, fallback = 0) => {
    const floor = minProxy(lot);
    const armed = proxyMaxFor(lot.id);
    // No floor to clamp to: the armed ceiling, or the drawer's own suggestion.
    if (floor === null) return armed ?? fallback;
    return Math.max(armed ?? fallback, floor);
  };

  // The armed proxy a card should ADVERTISE — the max only while it can still
  // fire. Once the price passes it the proxy is spent and will never auto-bid
  // again, so surfacing the ceiling would claim cover the dealer no longer has;
  // the card drops its PROXY chip and its OUTBID cue stands alone.
  //
  // The raw per-badge entry is deliberately left intact (clearProxy is the
  // only eraser) — it still floors the drawer via proxySeed.
  const armedProxy = (lot) => {
    const max = proxyMaxFor(lot.id);
    if (max == null) return undefined;
    // With no increment there is no next valid bid for the engine to measure
    // the ceiling against, so nothing has spent it: a prebid on an asset that
    // has not started is exactly the case, and it is the one most worth
    // advertising.
    const floor = minProxy(lot);
    return floor === null || max >= floor ? max : undefined;
  };

  // A proxy that can still fire IS the dealer's bid on that lot, so the live-bid
  // affordances come off: bidding live on top of your own max only raises the
  // price that max is then measured against, and the two read as contradictory
  // instructions with no way to tell which is in charge.
  //
  // A SPENT proxy (the price has already passed it) deliberately does not lock.
  // The engine will never act on it again, so holding live bidding shut would
  // trap the dealer behind a ceiling that no longer does anything — armedProxy
  // already draws exactly that line. Canceling stays available either way; it
  // keys off the raw per-badge entry, because a spent proxy is still a row on
  // the server.
  // The ceiling the dealer SET, spent or not — the raw entry rather than the
  // one that can still fire. A lane card needs both: it names the amount and
  // reports separately that the engine has dropped it, because the amount has to
  // survive being spent (reading the ceiling you set is how you raise it).
  const proxyAmount = (lot) => proxyMaxFor(lot.id) ?? null;

  // A proxy that exists and can no longer fire. The engine's test is whether it
  // can reach the NEXT valid bid, which is why this cannot be `current > max`:
  // at $25,300 with a $200 increment a $25,400 proxy is already out of
  // contention while that comparison still reads false — an increment-wide band
  // where a card would advertise cover the dealer does not have.
  const isProxySpent = (lot) => proxyAmount(lot) !== null && armedProxy(lot) == null;

  const proxyLocked = (lot) => armedProxy(lot) != null;

  // The "bid higher" chips: multiples of THIS lot's real increment. Keyed by the
  // multiplier so a selection survives switching to a lot with a different
  // increment.
  // `null` with no increment: there is no ladder, and a surface draws a dash
  // where the chips would be rather than four multiples of a figure nobody set.
  const bidChips = (lot) => {
    const increment = incFor(lot);
    if (increment === null) return null;
    return BID_INCREMENT_MULTIPLIERS.map((multiplier) => ({ multiplier, amount: multiplier * increment }));
  };
  // The amount a chip commits: the normal next bid, plus (multiplier − 1) extra
  // increments. Every result is current + k·increment (k ≥ 1), so it clears the
  // current bid AND satisfies the engine's "delta must be a multiple of the
  // increment" check.
  const raiseTo = (lot) => {
    const amount = bidAmount(lot);
    const increment = incFor(lot);
    // The opening ask is the whole of what can be bid with no increment, so no
    // multiple is applied to it — there is nothing to multiply.
    if (amount === null || increment === null) return amount;
    return amount + (bidIncrementMultiplier.value - 1) * increment;
  };

  // ── Grouping for the list: [{ sale, type, lanes: [{ name, lots }] }] ────
  const groupLots = (rows) => {
    const sales = [];
    const saleIndex = new Map();
    for (const lot of rows) {
      let sale = saleIndex.get(lot.sale);
      if (!sale) {
        sale = { sale: lot.sale, type: lot.type, lanes: [], _laneIndex: new Map() };
        saleIndex.set(lot.sale, sale);
        sales.push(sale);
      }
      let lane = sale._laneIndex.get(lot.lane);
      if (!lane) {
        lane = { name: lot.lane, lots: [] };
        sale._laneIndex.set(lot.lane, lane);
        sale.lanes.push(lane);
      }
      lane.lots.push(lot);
    }
    return sales.map(({ _laneIndex, ...s }) => s);
  };

  // groupLots() allocates a fresh tree on every run, so ANY mutation of `lots` —
  // splicing out a single finalized lot, say — hands every LanePager a brand-new
  // `lots` array. That's a prop change, so every mounted pager re-renders, even
  // the lanes that didn't change. Reuse the previous run's array for any lane
  // whose membership is identical, and only genuinely-changed lanes re-render.
  //
  // Array identity is what matters: the pager's prop is the lane's `lots` array,
  // and orderGroupsByStart() re-wraps the lane objects but passes that same array
  // straight through — so memoizing at the array level is enough, and the lane
  // objects are free to be recreated.
  const sameLaneMembership = (a, b) =>
    a.length === b.length && a.every((lot, i) => lot === b[i]);

  // One memo per caller: `visibleGroups` and `allGroups` group different inputs
  // under the same lane keys, so a shared cache would evict on every run.
  function createLaneLotsMemo() {
    let previous = new Map();
    return (groups) => {
      const next = new Map();
      for (const group of groups) {
        for (const lane of group.lanes) {
          const prior = previous.get(`${group.sale}::${lane.name}`);
          if (prior && sameLaneMembership(prior, lane.lots)) lane.lots = prior;
          next.set(`${group.sale}::${lane.name}`, lane.lots);
        }
      }
      previous = next;
      return groups;
    };
  }
  const memoAllLanes = createLaneLotsMemo();

  // Every lot in the same lane as `lot`, in run order — what the live video
  // view's run-list drawer shows. Reads the same laneIndex the on-block marker
  // does, so the drawer and the marker can never disagree about a lane's
  // membership.
  const lotsInLane = (lot) => (lot ? laneIndex.value.get(laneKey(lot)) ?? [] : []);

  const allGroups = computed(() => memoAllLanes(groupLots(lots.value)));

  const saleNames = computed(() => [...new Set(lots.value.map((l) => l.sale))]);

  // The sales the runtime currently reports as RUNNING — a lot the clerk has not
  // left scheduled is a lane that is under way. This is the runtime's own answer
  // to "what is live", and every surface that badges a sale live reads it from
  // here (Home's Premier hero, the search results feed, the dashboard) rather
  // than re-deriving it, so two screens cannot disagree about one sale. Empty
  // when the runtime has said nothing, which is when the schedule answers.
  const liveSaleNames = computed(() => {
    const names = new Set();
    for (const lot of lots.value) {
      if (!lot.scheduled) names.add(lot.sale);
    }
    return names;
  });

  // Sales that are running AND hold at least one liked lot — the set the Live
  // tab's quick-switcher auto-pins. Sale-level AND: a liked lot may itself be
  // scheduled, but the sale still counts as long as any lane is live. Reactive
  // over `lots` + saved.likedAssetIds (via isLiked).
  const salesWithLikes = computed(() => {
    const live = new Set();
    const liked = new Set();
    for (const lot of lots.value) {
      if (!lot.scheduled) live.add(lot.sale);
      if (isLiked(lot)) liked.add(lot.sale);
    }
    const out = new Set();
    for (const s of liked) if (live.has(s)) out.add(s);
    return out;
  });

  // A liked lot rotating onto the block in ANY sale — even one the dealer
  // isn't viewing — fires a one-time alert and publishes lastAlert so the page
  // can offer a jump. Lots already on the block at load are pre-seeded into
  // liveNotified by loadLots, so this only fires on a genuine mid-session
  // rotation (a lane's head advancing after a hammer), never a load barrage.
  watch(onBlockByLane, (next) => {
    for (const lotId of next.values()) {
      if (liveNotified.has(lotId)) continue;
      const lot = liveById.get(lotId);
      if (!lot || !isLiked(lot)) continue;
      liveNotified.add(lotId);
      setLastAlert(lotId, 'likedLive');
      useNotifications().notify('bidding.likedLive');
    }
  });

  // The header "N LIVE" pill counts genuinely-live lots only: finalized lots stay
  // in the lanes (and in the stat-card counts) but are no longer live.
  const liveCount = computed(
    () => lots.value.filter((l) => !l.scheduled && !FINALIZED.has(l.engineStatus)).length
  );
  // ── Data loading + realtime ─────────────────────────────────────────────
  // Pull the live auction events from the runtime and hydrate the lot list +
  // initial bid state. Register each lot's vehicle card so saved / vehicle
  // detail deep-links hydrate without a separate fetch.
  async function loadLots({ force = false } = {}) {
    if (loaded.value && !force) return;
    if (loading.value) return;
    loading.value = true;
    loadFailed.value = false;
    try {
      // The identities ride the session; the proxy-bid read is scoped to their badges.
      bidIdentities.value = sessionStore().user?.bidderIdentities ?? [];
      const badges = bidIdentities.value.map((identity) => identity.badge).filter(Boolean);
      const { lots: rows = [], proxyBids = {} } = (await biddingSource.loadLots({ badges })) ?? {};
      const mapped = rows.map(mapServerLot);
      // Retain EVERY lot (finalized included) for the Bid Stack; Live Bidding
      // now keeps them too — a sold/passed/skipped lot stays in its lane with its
      // final result rather than vanishing (`lots` is a distinct array over the
      // same object refs so the two never alias on a mutation).
      engineLots.value = mapped;
      // Index the REACTIVE elements (not the raw `mapped` objects) so in-place
      // frame mutations via engineById trigger reactivity.
      engineById.clear();
      for (const l of engineLots.value) engineById.set(l.id, l);
      lots.value = [...mapped];
      // Index the live subset before seeding bids below — setLiveBid() consults
      // liveById to decide whether a lot counts toward the winning/outbid views.
      liveById.clear();
      winningIds.clear();
      outbidIds.clear();
      // A notice belongs to the block it was raised in.
      for (const k of Object.keys(incrementChanges)) delete incrementChanges[k];
      for (const l of lots.value) liveById.set(l.id, l);
      // Seed event status from the same rows. Keys are only ever added/updated —
      // a sale that dropped out of the feed keeps its last known status rather
      // than silently reverting to "running".
      for (const row of rows) {
        if (!row.eventId || !row.eventStatus) continue;
        eventStatuses[row.eventId] = row.eventStatus;
        if (row.eventHaltReason) eventHaltReasons[row.eventId] = row.eventHaltReason;
        else delete eventHaltReasons[row.eventId];
      }
      hydrateProxies(proxyBids);
      // AFTER hydrateProxies: statusFromBadge reads badgeFor(), which a hydrated
      // proxy may have moved to the dealership that armed it, and it answers null
      // for any lot outside `myBids` — which hydration also fills in.
      for (const row of rows) {
        if (row.bid != null) {
          setLiveBid(row.id, row.bid, statusFromBadge(row.id, row.badge));
        }
      }
      // Pre-mark lots already on the block as alerted, so the on-block watcher
      // fires only for genuine mid-session rotations, not this initial hydrate.
      for (const lotId of onBlockByLane.value.values()) {
        const lot = liveById.get(lotId);
        if (lot && isLiked(lot)) liveNotified.add(lotId);
      }
      saved.registerAssets?.(mapped.map(assetCardForLot));
      loaded.value = true;
    } catch (e) {
      useLogger().error({ event: 'bidding.load_failed', err: e }, 'live auction events could not be loaded');
      loadFailed.value = true;
    } finally {
      loading.value = false;
    }
  }

  // Adopt the proxies the runtime already holds for this dealer (see
  // biddingSource.loadLots). Without this `maxBids` is session-local: a reload
  // leaves the engine auto-bidding a max the app can neither show nor cancel.
  //
  // The map is keyed by itemId, and a lot's `id` IS its itemId (buildRow), so it
  // keys `maxBids` directly. A dealer holds several badges and can have armed a
  // proxy under EACH of them on the same lot, so every badge's row is kept —
  // collapsing them to the highest one lost the others, and a later clearProxy
  // sent a badge the runtime held nothing under (deleteProxyBid matches on badge
  // and silently no-ops).
  //
  // The dealer's own proxies out of one lot's runtime rows, keyed by badge. A
  // badge the dealer cannot bid as is dropped: the "bidding as" switcher cannot
  // name it, so there would be no way to cancel it from here.
  function proxyMapFromEntries(entries) {
    const byBadge = {};
    for (const entry of entries ?? []) {
      if (entry?.amount == null || entry.badge == null) continue;
      if (!bidIdentities.value.some((identity) => identity.badge === entry.badge)) continue;
      // Two rows under one badge should not happen; the highest max is the one
      // the engine acts on.
      if (byBadge[entry.badge] == null || entry.amount > byBadge[entry.badge]) {
        byBadge[entry.badge] = entry.amount;
      }
    }
    return byBadge;
  }
  // The dealership holding the biggest max on a lot — the one actually doing the
  // bidding, and so the lot's identity when the dealer has not picked one.
  function topProxyIdentity(byBadge) {
    let topBadge = null;
    for (const [badge, amount] of Object.entries(byBadge)) {
      if (topBadge == null || amount > byBadge[topBadge]) topBadge = badge;
    }
    return bidIdentities.value.find((identity) => identity.badge === topBadge) ?? null;
  }
  function hydrateProxies(proxyBids) {
    for (const [itemId, entries] of Object.entries(proxyBids || {})) {
      if (!Array.isArray(entries)) continue;
      const byBadge = proxyMapFromEntries(entries);
      if (!Object.keys(byBadge).length) continue;
      maxBids[itemId] = byBadge;
      // A pick the dealer already made for the lot still wins.
      if (!identities[itemId]) {
        const identity = topProxyIdentity(byBadge);
        if (identity) identities[itemId] = identity;
      }
      myBids.add(itemId);
    }
  }

  // Map a runtime high-bid badge to this dealer's winning/outbid status. Lots
  // the dealer never bid on show no winning/outbid badge (null).
  function statusFromBadge(id, badge) {
    if (!myBids.has(id)) return null;
    return badge === badgeFor(id) ? 'winning' : 'outbid';
  }

  // Apply a `lot.updated` frame from the runtime to live state.
  function applyLotFrame(frame) {
    if (!frame?.lotId) return;
    // The delta socket carries a single global `item-bidding` topic with no
    // per-event filter, so frames for lots outside this block arrive too (and
    // frames for the previous block keep arriving after a reset). Drop them here:
    // everything below is then a Map lookup plus a few in-place writes, which is
    // what makes "subscribe to everything, render only the window" cheap.
    if (!engineById.has(frame.lotId)) return;
    const id = frame.lotId;
    const lot = findLot(id);
    // Keep the retained engine record live — engineStatus/badge/current persist
    // even after the lot is spliced out of `lots` below, so the Bid Stack can
    // show its final Won/Lost.
    const el = engineById.get(id);
    if (el) {
      if (frame.engineStatus != null) el.engineStatus = frame.engineStatus;
      if (frame.badge != null) el.badge = frame.badge;
      if (frame.bid != null || frame.current != null) el.currentBid = frame.bid ?? frame.current;
    }
    // Snapshot the pre-frame status so we can detect a winning→outbid flip.
    const prevStatus = liveBids[id]?.status ?? null;
    if (frame.current != null || frame.bid != null) {
      setLiveBid(id, (frame.bid ?? frame.current), statusFromBadge(id, frame.badge));
    }
    const nextStatus = liveBids[id]?.status ?? null;
    // A bid of this dealer's is inside its confirmation window: keep what this
    // frame reports, because that pair is the ENGINE's answer and the store's
    // own entry is not — placeBid wrote the dealer's own figure into it
    // optimistically. See BID_CONFIRM_DELAYS_MS.
    const pending = pendingBids[id];
    if (pending && (frame.bid != null || frame.current != null)) {
      pending.frame = { bid: frame.bid ?? frame.current, badge: frame.badge ?? null };
    }
    // The dealer just lost the lead on a lot they're bidding.
    //
    // …unless this frame is a REJECTION'S ECHO. A refused proposal still adds
    // the badge to `activeBidders`, which is a document change, so a frame
    // comes carrying the OLD price under the other dealer's badge — and the
    // dealer who was never in the lead is told they lost it, with the outbid
    // sound. A frame at or under the figure just sent, inside that window, is
    // that echo rather than a competing bid; placeBid undoes the face and
    // reports it as `bid.rejected`.
    if (prevStatus !== 'outbid' && nextStatus === 'outbid' && !isRejectionEcho(pending)) {
      useLogger().info(
        {
          event: 'outbid.received',
          assetId: id,
          saleId: lot?.eventId ?? null,
          currentBid: liveBids[id]?.current ?? null,
          proxyArmed: proxyMaxFor(id) != null
        },
        'dealer lost the lead'
      );
      useNotifications().notify('bidding.outbid');
      setLastAlert(id, 'outbid');
    }
    if (lot) {
      if (frame.increment != null) {
        const previous = lot.bidIncrement;
        lot.bidIncrement = frame.increment;
        // A change the dealer can be surprised by: the asset HAD an increment
        // and the clerk moved it. An asset that had none is one the engine has
        // just started, which is a ladder appearing rather than changing, and
        // nothing was on screen to contradict.
        if (previous != null && previous !== frame.increment) {
          incrementChanges[id] = { previous, next: frame.increment, at: Date.now() };
          useLogger().info(
            {
              event: 'increment.changed',
              assetId: id,
              saleId: lot.eventId ?? null,
              previous,
              next: frame.increment
            },
            'the sale changed the bid increment'
          );
        }
      }
      if (frame.estimatedTime?.utcTime) {
        lot.endsAt = Date.parse(frame.estimatedTime.utcTime);   // anti-snipe extensions flow through here
        lot.secondsRemaining = Math.max(0, Math.round((lot.endsAt - Date.now()) / 1000));
        // A lot the dealer cares about is about to hammer — fire once.
        const cares = myBids.has(id) || nextStatus === 'winning';
        if (cares && lot.secondsRemaining > 0 && lot.secondsRemaining <= ENDING_SOON_SECS && !endingNotified.has(id)) {
          endingNotified.add(id);
          useNotifications().notify('bidding.ending');
          setLastAlert(id, 'ending');
        }
      }
    }
    // Lot finished. It STAYS in its lane (kept visible with its final
    // won/lost/sold/skipped result — see lotResult); the on-block marker moves on
    // to the next non-finalized lot via onBlockByLane, and its winning/outbid
    // membership persists so the finalized lot still counts.
    if (FINALIZED.has(frame.engineStatus)) {
      // Hammer fell in the dealer's favor (their badge is the high bid) — fire once.
      if (frame.badge && frame.badge === badgeFor(id) && !wonNotified.has(id)) {
        wonNotified.add(id);
        useNotifications().notify('bidding.won');
        setLastAlert(id, 'won');
      }
      // The outcome, reported once per asset the dealer actually bid on. The
      // result is read back off `lotResult` rather than recomputed here, so
      // there is one definition of won/lost/no-sale and the log cannot come to
      // disagree with what the Bid Stack shows.
      const finishedLot = el || lot;
      if (finishedLot && myBids.has(id) && !resultLogged.has(id)) {
        const result = lotResult(finishedLot);
        if (result) {
          resultLogged.add(id);
          useLogger().info(
            {
              event: `asset.${result === 'noSale' ? 'no_sale' : result}`,
              assetId: id,
              saleId: finishedLot.eventId ?? null,
              engineStatus: frame.engineStatus,
              finalBid: liveBids[id]?.current ?? finishedLot.currentBid ?? null,
              proxyArmed: proxyMaxFor(id) != null
            },
            `asset finished as ${result}`
          );
        }
      }
    }
  }

  // Apply an `event-activity` frame — the clerk paused, resumed or finished the
  // sale. One key per event, so every lot in it re-reads at once.
  function applyEventFrame(frame) {
    if (!frame?.eventId || !frame.status) return;
    eventStatuses[frame.eventId] = frame.status;
    // The reason rides a SECOND frame (the first carries the status alone so the
    // board halts without waiting on a read). A frame without one means the halt
    // is over, or its reason hasn't landed yet — either way the stale reason from
    // a previous halt must not survive.
    if (frame.reason) eventHaltReasons[frame.eventId] = frame.reason;
    else delete eventHaltReasons[frame.eventId];
  }

  // Drop stale live state and pull a fresh block. Used after a demo reset — the
  // runtime has no `lots.reset` broadcast to trigger a reload for us.
  async function reloadBlock() {
    for (const k of Object.keys(eventStatuses)) delete eventStatuses[k];
    for (const k of Object.keys(eventHaltReasons)) delete eventHaltReasons[k];
    for (const k of Object.keys(liveBids)) delete liveBids[k];
    for (const k of Object.keys(maxBids)) delete maxBids[k];
    for (const k of Object.keys(incrementChanges)) delete incrementChanges[k];
    winningIds.clear();
    outbidIds.clear();
    myBids.clear();
    wonNotified.clear();
    resultLogged.clear();
    endingNotified.clear();
    liveNotified.clear();
    loaded.value = false;
    await loadLots({ force: true });
    resetSeq.value += 1;        // signal: a fresh sale is loaded
  }

  const subscriptions = [];
  function subscribeRealtime() {
    if (subscriptions.length) return;
    subscriptions.push(biddingSource.subscribe({
      onLotFrame: (frame) => applyLotFrame(frame),
      onEventFrame: (frame) => applyEventFrame(frame)
    }));
    if (!tickHandle) tickHandle = setInterval(() => { nowTick.value = Date.now(); }, 1000);
  }

  // Realtime is refcounted across the surfaces that need frames, because this
  // store is a singleton: subscribing once at setup and tearing down on any one
  // consumer's unmount killed the socket AND the countdown tick for the rest of
  // the session, and setup never ran again to re-subscribe. Leaving Live Bidding
  // and coming back left a frozen page.
  //
  // Callers take a handle in onMounted and call the returned release in
  // onUnmounted; the socket lives as long as at least one holder does.
  let realtimeRefs = 0;
  function acquireRealtime() {
    realtimeRefs += 1;
    subscribeRealtime();        // self-guards on subscriptions.length
    let released = false;       // a double-release must not drop someone else's ref
    return () => {
      if (released) return;
      released = true;
      realtimeRefs -= 1;
      if (realtimeRefs <= 0) teardown();
    };
  }

  // ── Bid confirmation ───────────────────────────────────────────────────
  // What makes a bid real is the engine taking it, and the engine never says so
  // to the client. It rules on every proposal, and a REFUSED one changes
  // nothing on the item-bidding document except its own transcript — which the
  // delta projection strips. The write's 2xx says the entry was recorded, not
  // that the engine took it. So the only evidence a bid landed is the price
  // moving to this dealer's badge.
  //
  // placeBid shows the lead first (the dealer's thumb is still on the control),
  // then watches for that evidence, and puts the face back when it never comes.
  // Confetti and the placed blip wait for it too: they are the app saying the
  // bid is in, and until the engine rules nobody knows that.
  //
  // The shape is setProxy's, one window shorter. A proxy can wait 3.75 s
  // because nothing is calling; a live bid is placed while an auctioneer is,
  // and a dealer must not read "You lead" for longer than it takes to look up.
  const BID_CONFIRM_DELAYS_MS = [250, 500, 1250];
  // id -> count of bid writes started for that asset. Only the LATEST write's
  // confirmation may reconcile the store; an older one would undo a newer bid.
  const bidWrites = {};
  // id -> { amount, badge, write, frame } while a bid is inside its window.
  // `frame` is what the last bid-carrying frame reported. Plain rather than
  // reactive: nothing renders it (a "Placing…" face is a design change Josh
  // holds) and every read is inside an action.
  const pendingBids = {};
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // A frame at or under the figure just sent, while that bid is pending, is the
  // rejection's echo — see applyLotFrame, which is the only caller.
  const isRejectionEcho = (pending) =>
    pending?.frame != null && pending.frame.bid != null && pending.frame.bid <= pending.amount;

  // One `{ bid, badge }` pair, ruled against the bid that was sent.
  //
  //   confirmed  — this dealer's badge holds at or above the figure sent.
  //   superseded — the price is PAST the figure under another badge: the engine
  //                took the bid and something beat it (a proxy fires in the
  //                same engine step), so the bid was placed and the frame's
  //                own outbid cue has already said the rest.
  //   rejected   — the price did not pass the figure, or nothing moved at all.
  function ruleOnBid(pair, { amount, badge }) {
    if (!pair) return 'rejected';
    if (pair.badge != null && pair.badge === badge && pair.bid != null && pair.bid >= amount) return 'confirmed';
    if (pair.bid != null && pair.bid > amount) return 'superseded';
    return 'rejected';
  }

  // 'confirmed' | 'superseded' | 'rejected' | 'unknown'.
  //
  // Frames are what move this: every tick re-reads the pair the last frame
  // carried. The single read at the end is for the case no frame can cover —
  // the engine refused the proposal and the document never changed, so there is
  // nothing to broadcast and nothing to wait for. A frame that DID answer is
  // the engine's answer already, so no read is spent to be told it twice.
  //
  // 'unknown' is the proxy path's rule read the other way: a lost read must not
  // tell the dealer their bid was refused.
  async function confirmBid(lot, pending) {
    for (const delayMs of BID_CONFIRM_DELAYS_MS) {
      await wait(delayMs);
      const verdict = ruleOnBid(pending.frame, pending);
      if (verdict !== 'rejected') return verdict;
    }
    if (pending.frame) return 'rejected';
    try {
      const doc = await biddingSource.getItemBidding(lot.itemId);
      return ruleOnBid({ bid: doc?.bid ?? null, badge: doc?.badge ?? null }, pending);
    } catch (e) {
      useLogger().warn(
        {
          event: 'bid.confirm_failed',
          assetId: lot.id,
          saleId: lot.eventId ?? null,
          amount: pending.amount,
          badge: pending.badge,
          err: e
        },
        'bid could not be read back'
      );
      return 'unknown';
    }
  }

  // ── Live actions ───────────────────────────────────────────────────────
  // Dealer takes the lead. Optimistically reflects the bid, writes it to the
  // runtime, then reads the price back and undoes the lead when the engine
  // never took it. Answers `true` when the bid was placed (confirmed, beaten
  // in the same step, or a read that could not say), `false` on a refusal —
  // which is what lets a caller tell the dealer. It never throws.
  async function placeBid(id, amount) {
    const lot = findLot(id);
    if (!lot) return;
    // The engine isn't processing bids on a paused or finished sale, so a bid
    // placed here would be optimistically shown as winning and then never
    // confirmed. The UI disables its bid affordances too — this is the backstop
    // for a sale that stops between the tap and the write.
    if (isBiddingUnavailable(lot)) return;
    // An armed proxy is already bidding this lot — see proxyLocked. Every
    // surface takes its live-bid control away, so this is the backstop for a
    // proxy armed on another device (or in the video dock) between the tap and
    // the write.
    if (proxyLocked(lot)) return;
    // The dealer already holds the high bid, so this would raise their own
    // price against nobody. Every surface takes its live-bid control away while
    // winning, which makes this the backstop for the likeliest race of the
    // three: bid frames arrive constantly, so the one that flips this dealer to
    // winning can land between the tap and the write.
    if (effStatus(lot) === 'winning') return;
    const amt = amount ?? bidAmount(lot);
    const badge = badgeFor(id);
    // The face to put back if the engine refuses it. An asset nobody has bid on
    // holds NO entry, and a refused bid has to leave it holding none.
    const snapshot = liveBids[id] ? { current: liveBids[id].current, status: liveBids[id].status } : null;
    const heldBefore = myBids.has(id);
    const write = (bidWrites[id] = (bidWrites[id] ?? 0) + 1);
    const pending = { amount: amt, badge, write, frame: null };
    pendingBids[id] = pending;
    myBids.add(id);
    setLiveBid(id, amt, 'winning');
    // Only the latest write may undo: a second bid inside the window has
    // already replaced this face, and putting the pre-FIRST-bid price back
    // would undo a bid the engine may well have taken.
    const undo = () => {
      if (bidWrites[id] !== write) return;
      delete pendingBids[id];
      if (snapshot) setLiveBid(id, snapshot.current, snapshot.status);
      else clearLiveBid(id);
      if (!heldBefore) myBids.delete(id);
    };
    const proposedAt = requestTimestamp();
    useLogger().info(
      { event: 'bid.proposed', assetId: id, saleId: lot.eventId ?? null, amount: amt, badge },
      'dealer proposed a bid'
    );
    try {
      await biddingSource.proposeBid(lot.itemId, { amount: amt, badge });
      // Accepted BY THE RUNTIME — it took the proposal. Whether the ENGINE took
      // it is the read-back below; `durationMs` is the tap-to-ack the dealer
      // feels while the button is still under their thumb.
      useLogger().info(
        {
          event: 'bid.accepted',
          assetId: id,
          saleId: lot.eventId ?? null,
          amount: amt,
          badge,
          durationMs: Math.round(requestTimestamp() - proposedAt)
        },
        'runtime accepted the bid'
      );
    } catch (e) {
      useLogger().error(
        {
          event: 'bid.rejected',
          assetId: id,
          saleId: lot.eventId ?? null,
          amount: amt,
          badge,
          err: e,
          durationMs: Math.round(requestTimestamp() - proposedAt)
        },
        'runtime rejected the bid'
      );
      undo();
      return false;
    }
    const verdict = await confirmBid(lot, pending);
    if (bidWrites[id] === write) delete pendingBids[id];
    if (verdict === 'rejected') {
      undo();
      useLogger().error(
        {
          event: 'bid.rejected',
          assetId: id,
          saleId: lot.eventId ?? null,
          amount: amt,
          badge,
          reason: 'not_held_after_write'
        },
        'the engine did not take the bid'
      );
      return false;
    }
    // A read that could not answer leaves the face alone and says nothing to
    // the dealer — `bid.confirm_failed` is already in the log.
    if (verdict === 'unknown') return true;
    // Confetti is for LEADING, and `superseded` is not leading: the engine took
    // the bid and a proxy or a faster dealer passed it in the same step, which
    // the frame's own outbid cue has already said. The blip fires either way —
    // the bid went in.
    if (verdict === 'confirmed') confettiBump.value += 1;
    useNotifications().notify('bidding.placed');   // soft confirmation blip for the dealer's own bid
    return true;
  }

  // Both of these show the change first and write it second, and both put it
  // back if the write is refused. The optimism is no longer only cosmetic: an
  // armed max takes the live-bid controls away (proxyLocked), so a max the
  // runtime never accepted would otherwise leave the dealer unable to bid a lot
  // they have no cover on.
  //
  // A 200 only means the runtime took the request: the engine decides later, and
  // a refusal (an asset that already ran, or one that is starting) never comes
  // back on any feed. So after the write, both read the lot's proxy bids back
  // until the runtime shows the change, and when it never does they adopt what
  // the runtime holds.
  //
  // setProxy answers whether the runtime holds the proxy: `true` when the read
  // shows it (or the read itself failed — the write was taken, and a lost read
  // must not tell the dealer they are uncovered), `false` when the write or the
  // read-back says no — and also for a lot the store cannot find, since nothing
  // was written. It never throws; callers that fire-and-forget stay safe, and
  // callers that await can tell the dealer.
  //
  // clearProxy answers the mirror question — whether the runtime LET GO of the
  // proxy: `true` when the read no longer shows the badge, when the read itself
  // failed (same rule as setProxy, read the other way), and for a lot the store
  // holds no runtime lot for, where the drop is local only and there was never
  // anything to let go of. `false` when the delete write or the read-back says
  // the runtime still bids it. It never throws either. The answer is what lets
  // a caller refuse to drop a card whose proxy is still live.
  const PROXY_CONFIRM_DELAYS_MS = [250, 500, 1000, 2000];
  // id -> count of proxy writes started for that lot. Only the latest write's
  // read-back may reconcile the store; an older one would undo a newer change.
  const proxyWrites = {};

  // { confirmed: true|false, entries } — or { confirmed: null } when a read fails.
  async function readBackProxy(lot, isExpected) {
    const badges = bidIdentities.value.map((identity) => identity.badge).filter(Boolean);
    let entries = [];
    for (const delayMs of PROXY_CONFIRM_DELAYS_MS) {
      await wait(delayMs);
      try {
        entries = await biddingSource.getProxyBids(lot.itemId, { badges });
      } catch (e) {
        useLogger().warn(
          { event: 'proxy.confirm_failed', assetId: lot.id, saleId: lot.eventId ?? null, err: e },
          'proxy bid could not be read back'
        );
        return { confirmed: null };
      }
      if (isExpected(entries)) return { confirmed: true, entries };
    }
    return { confirmed: false, entries };
  }
  // Make one lot's maxes match what the runtime holds for this dealer — every
  // badge of theirs, not only the one the refused write was sent under.
  function adoptProxyFor(id, entries) {
    const byBadge = proxyMapFromEntries(entries);
    if (Object.keys(byBadge).length) maxBids[id] = byBadge;
    else delete maxBids[id];
  }

  async function setProxy(id, max) {
    const lot = findLot(id);
    const badge = badgeFor(id);
    const previous = proxyMaxForBadge(id, badge);
    const write = (proxyWrites[id] = (proxyWrites[id] ?? 0) + 1);
    writeProxyMax(id, badge, max);
    myBids.add(id);
    if (lot) {
      try {
        await biddingSource.upsertProxyBid(lot.itemId, { eventId: lot.eventId, amount: max, badge });
      } catch (e) {
        useLogger().error(
          { event: 'proxy.rejected', assetId: id, saleId: lot.eventId ?? null, amount: max, err: e },
          'proxy bid was refused'
        );
        if (previous == null) dropProxyMax(id, badge);
        else writeProxyMax(id, badge, previous);
        return false;
      }
      const readBack = await readBackProxy(lot, (entries) =>
        entries.some((entry) => entry?.badge === badge && entry.amount === max)
      );
      if (readBack.confirmed === false) {
        if (proxyWrites[id] === write) adoptProxyFor(id, readBack.entries);
        useLogger().error(
          { event: 'proxy.rejected', assetId: id, saleId: lot.eventId ?? null, amount: max, reason: 'not_held_after_write' },
          'proxy bid was refused'
        );
        return false;
      }
      useLogger().info(
        {
          event: 'proxy.set',
          assetId: id,
          saleId: lot.eventId ?? null,
          amount: max,
          previousAmount: previous ?? null,
          badge
        },
        'proxy bid armed'
      );
      return true;
    }
    // No lot to send it to: nothing was armed, so nothing may look armed.
    if (previous == null) dropProxyMax(id, badge);
    else writeProxyMax(id, badge, previous);
    return false;
  }
  async function clearProxy(id, { badge: named } = {}) {
    const lot = findLot(id);
    const badge = named ?? badgeFor(id);
    const previous = proxyMaxForBadge(id, badge);
    const write = (proxyWrites[id] = (proxyWrites[id] ?? 0) + 1);
    dropProxyMax(id, badge);
    if (lot) {
      try {
        await biddingSource.deleteProxyBid(lot.itemId, { eventId: lot.eventId, badge });
      } catch (e) {
        useLogger().error(
          { event: 'proxy.clear_rejected', assetId: id, saleId: lot.eventId ?? null, badge, err: e },
          'proxy bid could not be cleared'
        );
        // The runtime still holds it, so the app has to keep saying so — the
        // dealer is still covered, and still needs a way to cancel.
        if (previous != null) writeProxyMax(id, badge, previous);
        return false;
      }
      const readBack = await readBackProxy(lot, (entries) =>
        !entries.some((entry) => entry?.badge === badge)
      );
      if (readBack.confirmed === false) {
        // Same reason as above: the engine still bids it, so the max comes back.
        if (proxyWrites[id] === write) adoptProxyFor(id, readBack.entries);
        useLogger().error(
          { event: 'proxy.clear_rejected', assetId: id, saleId: lot.eventId ?? null, badge, reason: 'not_held_after_write' },
          'proxy bid could not be cleared'
        );
        return false;
      }
      useLogger().info(
        { event: 'proxy.cleared', assetId: id, saleId: lot.eventId ?? null, amount: previous ?? null, badge },
        'proxy bid cleared'
      );
      return true;
    }
    // No lot to send it to: the drop is local only, and it is already done.
    return true;
  }
  // Every badge's proxy off one lot. The clears run one after another, never in
  // parallel: `proxyWrites[id]` lets only the LATEST write for a lot reconcile
  // the store, so overlapping siblings would throw away each other's read-back.
  async function clearAllProxies(id) {
    const badges = Object.keys(proxyMaxesFor(id) ?? {});
    let ok = true;
    for (const badge of badges) ok = (await clearProxy(id, { badge })) && ok;
    return ok;
  }

  function setIdentity(id, identity) { identities[id] = identity; }
  const identityFor = (id) => identities[id] || defaultIdentity.value || bidIdentities.value[0] || null;


  function teardown() {
    while (subscriptions.length) {
      const off = subscriptions.pop();
      if (typeof off === 'function') { try { off(); } catch { /* noop */ } }
    }
    if (tickHandle) { clearInterval(tickHandle); tickHandle = null; }
  }

  return {
    // state
    lots, engineLots, liveBids, maxBids, identities, loaded, loading, loadFailed, resetSeq, eventStatuses, eventHaltReasons,
    confettiBump, bidIncrementMultiplier, nowTick, incrementChanges,
    // Membership of the winning / outbid views. Exposed because the surface's
    // own stat filter reads them, and reading them is the whole reason they
    // exist — see the note where they are declared for what scanning `liveBids`
    // instead would cost.
    winningIds, outbidIds,
    // data
    loadLots, reloadBlock,
    // derivations
    laneKey, findLot, liveCurrent, effStatus, onBlock, isRunning, acceptsProxy, secsLeft, vehiclesAway, bidAmount, bidChips, raiseTo,
    incrementChangeFor,
    minProxy, proxySeed, armedProxy, proxyLocked, proxyAmount, isProxySpent,
    proxyMaxForBadge, proxyMaxesFor,
    eventStatusFor, isPaused, isEventFinished, isBiddingUnavailable, isHalted, saleStatusFor, saleStatusReasonFor, haltMeta, haltReasonFor,
    isLiked, isLikedForDisplay, isDealerBadge, dealerLeads, lotResult, allGroups, liveCount, lotsInLane,
    saleNames, liveSaleNames, salesWithLikes, lastAlert, identityFor, bidIdentities,
    // Lane grouping, exposed so a surface can group a FILTERED set of lots the
    // same way. `createLaneLotsMemo` is a factory rather than an instance for a
    // reason recorded where it is defined: two callers grouping different inputs
    // under the same lane keys would evict each other from a shared cache.
    groupLots, createLaneLotsMemo,
    // actions
    defaultIdentity, setDefaultIdentity,
    placeBid, setProxy, clearProxy, clearAllProxies, setIdentity, toggleLike, seedVehicleDetail,
    acquireRealtime, teardown
  };
});
